"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, BookOpen, Calendar, FileText, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import Reveal from "@/components/common/Reveal";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
const articlesCopy = {
  badge: {
    en: "Technical Knowledge Base",
    zh: "技术知识库",
    es: "Base de conocimientos técnicos",
    de: "Technische Wissensdatenbank",
    fr: "Base de connaissances techniques",
    pt: "Base de conhecimento técnico"
  },
  h2: {
    en: "Technical Articles and Specification Guides",
    zh: "技术文章与规范指南",
    es: "Artículos técnicos y guías de especificaciones",
    de: "Fachartikel und Spezifikationsleitfäden",
    fr: "Articles techniques et guides de spécifications",
    pt: "Artigos técnicos e guias de especificação"
  },
  lead: {
    en: "Review filtration engineering data, test standards, and wire mesh calculations.",
    zh: "查阅过滤工程数据、测试标准与金属丝网计算文档。",
    es: "Consulte datos de ingeniería de filtración, normas de ensayo y cálculos de malla de alambre.",
    de: "Prüfen Sie Filtrations-Engineeringdaten, Prüfnormen und Berechnungen zu Drahtgeweben.",
    fr: "Consultez les données d'ingénierie de filtration, les normes d'essai et les calculs de toiles métalliques.",
    pt: "Consulte dados de engenharia de filtração, normas de ensaio e cálculos de telas metálicas."
  },
  viewAll: {
    en: "View all resources",
    zh: "查看全部资源",
    es: "Ver todos los recursos",
    de: "Alle Ressourcen ansehen",
    fr: "Voir toutes les ressources",
    pt: "Ver todos os recursos"
  },
  specGuideTag: {
    en: "Specification guide",
    zh: "技术规范指南",
    es: "Guía de especificaciones",
    de: "Spezifikationsleitfaden",
    fr: "Guide de spécification",
    pt: "Guia de especificação"
  },
  readSpec: {
    en: "Read spec",
    zh: "阅读规范",
    es: "Leer especificación",
    de: "Spezifikation lesen",
    fr: "Lire la spécification",
    pt: "Ler especificação"
  },
  pageIndicator: {
    en: "Page {current} of {total}",
    zh: "第 {current} 页 / 共 {total} 页",
    es: "Página {current} de {total}",
    de: "Seite {current} von {total}",
    fr: "Page {current} sur {total}",
    pt: "Página {current} de {total}"
  },
  defaultCategory: {
    en: "Technical Spec",
    zh: "技术规范",
    es: "Especificación técnica",
    de: "Technische Spezifikation",
    fr: "Spécification technique",
    pt: "Especificação técnica"
  }
};
const localeMap: Record<Lang, string> = {
  en: "en-US",
  zh: "zh-CN",
  es: "es-ES",
  de: "de-DE",
  fr: "fr-FR",
  pt: "pt-BR"
};
function ArticlesConsumer({
  lang
}: {
  lang: Lang;
}) {
  const {
    posts,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.from({
        length: 6
      }).map((_, i) => <div key={i} className="flex flex-col rounded-lg border border-border bg-card p-6 gap-4">
            <div className="flex items-center justify-between">
              <Skeleton className="h-5 w-24 rounded-sm" />
              <Skeleton className="h-4 w-16 rounded-sm" />
            </div>
            <Skeleton className="h-6 w-3/4 rounded-sm" />
            <Skeleton className="h-16 w-full rounded-sm" />
            <div className="pt-4 border-t border-border mt-auto flex justify-between items-center">
              <Skeleton className="h-4 w-20 rounded-sm" />
              <Skeleton className="h-8 w-24 rounded-md" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="rounded-lg border border-border bg-card p-8 text-center max-w-xl mx-auto">
        <p className="text-sm text-foreground font-medium mb-3">
          {pick(lang, COMMON.loadError)}
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          {pick(lang, {
          en: "Try again",
          zh: "重试",
          es: "Reintentar",
          de: "Erneut versuchen",
          fr: "Réessayer",
          pt: "Tentar novamente"
        })}
        </Button>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="rounded-lg border border-border bg-card p-10 text-center max-w-lg mx-auto">
        <FileText className="size-10 text-muted-foreground mx-auto mb-3 opacity-60" aria-hidden="true" />
        <h3 className="text-base font-semibold text-foreground mb-1">
          {pick(lang, COMMON.noResults)}
        </h3>
        <p className="text-sm text-muted-foreground mb-5">
          {pick(lang, {
          en: "Technical reference documents will be published here soon.",
          zh: "技术参考文档与规范资料即将在此发布。",
          es: "Los documentos técnicos de referencia se publicarán aquí próximamente.",
          de: "Technische Referenzdokumente werden hier in Kürze veröffentlicht.",
          fr: "Les documents techniques de référence seront bientôt publiés ici.",
          pt: "Documentos técnicos de referência serão publicados aqui em breve."
        })}
        </p>
        <Button asChild variant="outline" size="sm">
          <Link to="/TechnicalResourcesPage">
            {pick(lang, COMMON.relatedResources)}
          </Link>
        </Button>
      </div>;
  }
  return <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {posts.map((post, i) => {
        const categoryName = post.categories && post.categories.length > 0 ? post.categories[0].name : pick(lang, articlesCopy.defaultCategory);
        const formattedDate = post.date ? new Date(post.date).toLocaleDateString(localeMap[lang] || "en-US", {
          year: "numeric",
          month: "short",
          day: "numeric"
        }) : null;
        return <Reveal key={post.id} delay={Math.min(i * 60, 300)}>
              <Card className="overflow-hidden flex flex-col justify-between border-border bg-card transition-colors duration-200 hover:border-primary/40 h-full">
                <div>
                  {post.featuredImage && <div className="mf-card-media mf-ratio-card w-full">
                      <img src={post.featuredImage} alt={post.title || pick(lang, {
                  en: "Technical article cover",
                  zh: "技术文章封面",
                  es: "Portada del artículo técnico",
                  de: "Titelbild des Fachartikels",
                  fr: "Couverture de l'article technique",
                  pt: "Capa do artigo técnico"
                })} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    </div>}
                  
                  <CardHeader className="space-y-3 pb-3">
                    <div className="flex items-center justify-between gap-2">
                      <Badge variant="outline" className="font-mono text-sm px-2 py-0.5 tracking-tight font-normal bg-muted">
                        {categoryName}
                      </Badge>
                      {formattedDate && <span className="inline-flex items-center gap-1 text-sm text-muted-foreground font-mono">
                          <SafeHydrate><Calendar className="size-3.5" aria-hidden="true" /></SafeHydrate>
                          {formattedDate}
                        </span>}
                    </div>
                    <CardTitle className="text-lg font-semibold text-foreground tracking-tight leading-snug">
                      <Link to={post.link || "#"} className="hover:text-primary transition-colors line-clamp-2">
                        {post.title}
                      </Link>
                    </CardTitle>
                  </CardHeader>

                  {post.excerpt && <CardContent className="pt-0">
                      <div className="text-sm text-muted-foreground line-clamp-3 leading-relaxed" dangerouslySetInnerHTML={{
                  __html: post.excerpt
                }} />
                    </CardContent>}
                </div>

                <CardFooter className="pt-3 border-t border-border mt-auto flex items-center justify-between">
                  <span className="mf-caption text-muted-foreground font-medium">
                    {pick(lang, articlesCopy.specGuideTag)}
                  </span>
                  <Button asChild variant="ghost" size="sm" className="gap-1.5 px-2.5 text-primary hover:text-primary">
                    <Link to={post.link || "#"}>
                      <span>{pick(lang, articlesCopy.readSpec)}</span>
                      <ArrowRight className="size-3.5" aria-hidden="true" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </Reveal>;
      })}
      </div>

      {totalPages > 1 && <div className="flex items-center justify-between pt-6 border-t border-border">
          <p className="mf-caption text-muted-foreground font-mono">
            {pick(lang, articlesCopy.pageIndicator).replace("{current}", String(currentPage)).replace("{total}", String(totalPages))}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={prevPage} disabled={!hasPrev} className="gap-1 px-3">
              <ChevronLeft className="size-4" aria-hidden="true" />
              <span className="text-sm">{pick(lang, COMMON.previous)}</span>
            </Button>
            <Button variant="outline" size="sm" onClick={nextPage} disabled={!hasNext} className="gap-1 px-3">
              <span className="text-sm">{pick(lang, COMMON.next)}</span>
              <ChevronRight className="size-4" aria-hidden="true" />
            </Button>
          </div>
        </div>}
    </div>;
}
export default function RelatedArticles() {
  const {
    lang
  } = useLanguage();
  return <section id="relatedtechnicalarticles" data-nav="light" className="bg-background mf-section border-t border-border text-foreground">
      <div className="mf-container">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 pb-6 border-b border-border gap-4">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 text-sm font-mono tracking-wider text-muted-foreground uppercase mb-2">
                <BookOpen className="size-3.5 text-primary" aria-hidden="true" />
                <span>{pick(lang, articlesCopy.badge)}</span>
              </div>
              <h2 className="mf-h2 tracking-tight text-foreground font-default">
                {pick(lang, articlesCopy.h2)}
              </h2>
              <p className="text-sm md:text-base text-muted-foreground mt-2">
                {pick(lang, articlesCopy.lead)}
              </p>
            </div>

            <div className="shrink-0">
              <Button asChild variant="outline" size="sm" className="gap-2">
                <Link to="/TechnicalResourcesPage">
                  <span>{pick(lang, articlesCopy.viewAll)}</span>
                  <ArrowRight className="size-3.5" aria-hidden="true" />
                </Link>
              </Button>
            </div>
          </div>
        </Reveal>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 6,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }} className="w-full">
          <ArticlesConsumer lang={lang} />
        </PostsProvider>
      </div>
    </section>;
}